import Clickable from '@carvana/merch-components/Clickable';
import { useMap } from '@vis.gl/react-google-maps';
import cx from 'classnames';
import { useCallback } from 'react';
import type { GeocodeLocation, VendingMachineLocation } from '~/contracts/vm';
import {
  LegendLocationItemAddress,
  LegendLocationItemDistance,
  LegendLocationItemInnerContainer,
  LegendLocationItemTitle,
  LegendLocations,
  MapLegendListContainer
} from './MapLegend.styles';

type LocationWithDistance = VendingMachineLocation & {
  distance?: {
    hasError: boolean;
    mi: number;
  };
};

type MapLegendProps = {
  activeLocation: Partial<VendingMachineLocation>;
  vendingMachineLocations: LocationWithDistance[];
  setGeoCoords: (coords: GeocodeLocation & { label: string }) => void;
  updateActiveLocation: (location: LocationWithDistance) => void;
};

const makeHref = (location: VendingMachineLocation): string => {
  const { city, state } = location;

  return `/vending-machine/${city.replace(' ', '-')}-${state}`;
};

export default function MapLegend({
  activeLocation,
  vendingMachineLocations,
  setGeoCoords,
  updateActiveLocation
}: MapLegendProps) {
  const map = useMap();

  const locationItemClick = useCallback(
    (location: LocationWithDistance) => () => {
      if (!map) return;

      setGeoCoords(location);
      updateActiveLocation(location);

      const z = map.getZoom() ?? 0;

      if (z > 4.7) {
        map.setZoom(4.7);
      }

      setTimeout(() => {
        map.panTo({ lat: location.lat, lng: location.lng });
        map.setZoom(8);
      }, 800);
    },
    [map, setGeoCoords, updateActiveLocation]
  );

  return (
    <MapLegendListContainer>
      <LegendLocations>
        {vendingMachineLocations.map((location, i) => (
          <div
            key={`${location.label}-${i}`}
            className={cx(
              'relative mr-20 h-[136px] w-[295px] min-w-[295px] rounded-md lg:mr-0 lg:w-full lg:rounded-none',
              'border-b border-[#d9dde9] shadow-[0px_6px_13px_rgba(24,53,88,0.1)]',
              'font-brandon text-base',
              location.label === activeLocation.label ? 'bg-grey-1' : 'bg-white'
            )}
          >
            <Clickable
              aria-label={`Show ${location.label} on the map`}
              className="absolute inset-0 z-0"
              data-analytics-id="Location Item"
              data-testid="location-item"
              onClick={locationItemClick(location)}
            />
            <LegendLocationItemInnerContainer className="pointer-events-none relative z-10">
              <a
                href={makeHref(location)}
                className="pointer-events-auto"
                data-analytics-id={null}
                data-testid="location-link"
              >
                <LegendLocationItemTitle>
                  {(i + 1).toLocaleString()}. {location.label}
                </LegendLocationItemTitle>
              </a>
              <LegendLocationItemDistance>
                {location.distance && !location.distance.hasError
                  ? `${location.distance.mi.toFixed(0)} mi`
                  : ''}
              </LegendLocationItemDistance>
              <LegendLocationItemAddress>
                {location.address}
              </LegendLocationItemAddress>
              <LegendLocationItemAddress>
                {`${location.city}, ${location.state}  ${location.zip}`}
              </LegendLocationItemAddress>
            </LegendLocationItemInnerContainer>
          </div>
        ))}
      </LegendLocations>
    </MapLegendListContainer>
  );
}
